import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import DatabaseAuth from '../components/DatabaseAuth';

function DatabaseTest() {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAuthSuccess = (userData, businessData) => {
    console.log('Authentication successful:', userData, businessData);
    setUser(userData);
    setBusiness(businessData);
  };

  const fetchDatabaseData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/database/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDbData(data);
      } else {
        console.error('Failed to fetch database data');
      }
    } catch (error) {
      console.error('Error fetching database data:', error);
    } finally {
      setLoading(false);
    }
  };

  const inspectDatabase = async () => {
    setLoading(true);
    try {
      // This would call our inspect-database.js script via an API endpoint
      // For now, just show a message
      alert('Database inspection would show detailed table contents here. Check the backend logs for the inspect-database.js output.');
    } catch (error) {
      console.error('Error inspecting database:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4 }}>
          <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>
            🗄️ PostgreSQL Database Authentication Test
          </Typography>

          <Typography sx={{ mb: 4, textAlign: 'center', color: '#666' }}>
            Test the new business owner authentication system with PostgreSQL database integration
          </Typography>

          <DatabaseAuth onAuthSuccess={handleAuthSuccess} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4 }}>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>
          ✅ Database Authentication Successful!
        </Typography>

        {/* User Info */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              👤 Authenticated User
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography><strong>Name:</strong> {user.name}</Typography>
                <Typography><strong>Email:</strong> {user.email}</Typography>
                <Typography><strong>User Type:</strong> {user.userType}</Typography>
              </Grid>
              {business && (
                <Grid item xs={12} md={6}>
                  <Typography><strong>Business:</strong> {business.name}</Typography>
                  <Typography><strong>Type:</strong> {business.businessType}</Typography>
                  <Typography><strong>Industry:</strong> {business.industry}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Database Actions */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              🗄️ Database Operations
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={fetchDatabaseData}
                disabled={loading}
                sx={{ backgroundColor: '#6366f1' }}
              >
                📊 Check Database Status
              </Button>

              <Button
                variant="outlined"
                onClick={inspectDatabase}
                disabled={loading}
              >
                🔍 Inspect Database Tables
              </Button>

              <Button
                variant="outlined"
                onClick={() => window.open('https://dhandhapaani.store/api/database/products', '_blank')}
              >
                📦 View Products API
              </Button>

              <Button
                variant="outlined"
                onClick={() => window.open('https://dhandhapaani.store/api/database/customers', '_blank')}
              >
                👥 View Customers API
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Database Status */}
        {dbData && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                📊 Database Status
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                Database connection is working! PostgreSQL is properly configured.
              </Alert>
              <Typography>
                <strong>Status:</strong> {dbData.status || 'Connected'}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Database Schema Explanation */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              🏗️ Database Structure Overview
            </Typography>

            <Typography sx={{ mb: 2 }}>
              Your PostgreSQL database now contains these main tables:
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#6366f1' }}>
                    🏢 Core Tables
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip label="companies" size="small" />
                    <Chip label="users" size="small" />
                    <Chip label="dealers" size="small" />
                    <Chip label="customers" size="small" />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#10b981' }}>
                    📦 Business Tables
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip label="items" size="small" color="success" />
                    <Chip label="purchase_invoices" size="small" color="success" />
                    <Chip label="sales_invoices" size="small" color="success" />
                    <Chip label="stock_movements" size="small" color="success" />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Typography sx={{ mt: 3, fontSize: '0.9rem', color: '#666' }}>
              💡 <strong>How it works:</strong> When you authenticate, your Google Sheets data gets migrated to these structured PostgreSQL tables.
              Each table has proper relationships, constraints, and automatic calculations (like due amounts, stock levels, etc.).
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default DatabaseTest;